import DatabaseIcon from '@lib/core/src/assets/third-party-logos/database.svg?react';
import DriveIcon from '@lib/core/src/assets/third-party-logos/google-drive.svg?react';
import NotionIcon from '@lib/core/src/assets/third-party-logos/notion.svg?react';
import palette from '@lib/core/src/theme/palette';
import { Stack, Typography, Chip, styled } from '@mui/material';
import { RiCloseCircleFill } from '@remixicon/react';

import { UnifiedConnectionDtoProvider } from '@/api/generated';
import { Card, LargeTitle } from '@/components/AiWorkflow/AiWorkflowStyles';
import useConnections from '@/hooks/Integrations/useConnections';
import { useGoogleDriveActions, useSelectedGoogleDriveFiles } from '@/store/googleDriveStore';

const Subtitle = styled(Typography)({
  fontWeight: 500,
  fontSize: 13,
  color: palette.grey[500],
  marginBottom: 22
});

const KnowledgeContextFile = styled(Stack)({
  backgroundColor: palette.white,
  color: palette.black,
  border: `1px solid ${palette.grey[1200]}`,
  fontSize: '11px',
  height: '30px',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  borderRadius: '16px',
  padding: '0 8px',
  maxWidth: '200px',
  minWidth: 0,
  flexShrink: 1
});

const KnowledgeContextFileText = styled(Typography)({
  fontSize: 11,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0
});

const DataSourceRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  padding: 16,
  borderRadius: 8,
  height: 80,
  border: `1px solid ${palette.grey[1200]}`,
  background: `linear-gradient(90deg, #FFF 20.67%, #FCF7FF 100%)`
});

const DriveRow = styled(DataSourceRow)({
  background: 'linear-gradient(90deg, #FFF 20.67%, #F1F7FF 100%)',
  alignItems: 'center'
});

const NotionRow = styled(DataSourceRow)({
  background: 'linear-gradient(90deg, #FFF 20.67%, #F3F3F3 100%)',
  opacity: 0.5
});

const DataSourceText = styled(Typography)({
  fontWeight: 600,
  fontSize: 13,
  color: palette.black
});

const SoonTag = styled(Chip)({
  fontSize: 11,
  fontWeight: 600,
  borderRadius: 4,
  backgroundColor: palette.white,
  border: `1px solid ${palette.grey[200]}`,
  color: palette.grey[500],
  height: 22
});

const Row = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexGrow: 1,
  gap: 8,
  minWidth: 0,
  overflow: 'hidden'
});

const KnowledgeContext = () => {
  const { conntectionList } = useConnections();
  const selectedGoogleDriveFiles = useSelectedGoogleDriveFiles();

  const isGoogleDriveEnabled = conntectionList.some(
    connection => connection.provider === UnifiedConnectionDtoProvider.googleDrive
  );
  const { openGoogleDrive, clearSelectedGoogleDriveFiles } = useGoogleDriveActions();

  const handleDriveRowClick = () => {
    if (isGoogleDriveEnabled) {
      openGoogleDrive();
    }
  };

  const handleDriveFileChipClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    clearSelectedGoogleDriveFiles();
  };

  return (
    <Card>
      <LargeTitle>Knowledge Context</LargeTitle>
      <Subtitle>What data sources would you like to include?</Subtitle>
      <Stack gap={1}>
        <DataSourceRow>
          <Row>
            <DatabaseIcon />
            <DataSourceText>Journey Proprietary Data</DataSourceText>
          </Row>
        </DataSourceRow>
        <DriveRow
          sx={{
            opacity: isGoogleDriveEnabled ? 1 : 0.5,
            cursor: isGoogleDriveEnabled ? 'pointer' : 'default'
          }}
          onClick={handleDriveRowClick}
        >
          <Row>
            <DriveIcon />
            <DataSourceText>Google Drive</DataSourceText>
            {selectedGoogleDriveFiles.length > 0 && (
              <KnowledgeContextFile onClick={handleDriveFileChipClick}>
                <KnowledgeContextFileText>
                  {selectedGoogleDriveFiles[0].fileName ?? ''}
                </KnowledgeContextFileText>
                <RiCloseCircleFill size={16} color={palette.black} />
              </KnowledgeContextFile>
            )}
          </Row>
        </DriveRow>
        <NotionRow>
          <Row>
            <NotionIcon />
            <DataSourceText>Notion</DataSourceText>
          </Row>
          <SoonTag label="Soon" size="small" />
        </NotionRow>
      </Stack>
    </Card>
  );
};

export default KnowledgeContext;
